import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import Section from '../components/ui/Section';
import Button from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';

const TimelineItem = ({ year, title, desc, align, index, image, imageAlt }) => (
  <motion.div 
    initial={{ opacity: 0, y: 50 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-100px" }}
    transition={{ duration: 0.7, delay: index * 0.2 }}
    className="flex flex-col md:flex-row gap-8 md:gap-16 items-center mb-20 relative z-10"
  >
    {/* Text Side */}
    <div className={`w-full md:w-1/2 ${align === 'left' ? 'md:text-right order-2 md:order-1' : 'md:text-left order-2 md:order-3'}`}>
      <div className="bg-white p-8 rounded-2xl shadow-soft border border-gray-100 hover:shadow-card transition-all duration-300 relative group ml-8 md:ml-0">
        <span className="inline-block py-1 px-3 bg-secondary/10 text-secondary-dark font-bold rounded-lg mb-3 text-sm">{year}</span>
        <h4 className="text-2xl font-heading font-bold text-primary-dark mb-3">{title}</h4>
        <p className="text-gray-600 leading-relaxed">{desc}</p>
      </div>
    </div>
    
    {/* Center Dot */}
    <div className="order-1 md:order-2 absolute left-4 md:static md:flex items-center justify-center transform -translate-x-1/2 md:translate-x-0 -top-2 md:top-auto">
      <div className="w-4 h-4 md:w-8 md:h-8 bg-secondary rounded-full z-10 ring-4 md:ring-8 ring-white shadow-lg flex items-center justify-center">
        <div className="w-1.5 h-1.5 md:w-3 md:h-3 bg-white rounded-full"></div>
      </div>
    </div>

    {/* Image Side */}
    <div className={`w-full md:w-1/2 ${align === 'left' ? 'order-3 md:order-3' : 'order-3 md:order-1'} pl-8 md:pl-0`}>
       <div className="rounded-2xl overflow-hidden shadow-lg aspect-video relative group">
          <div className="absolute inset-0 bg-primary-dark/10 group-hover:bg-transparent transition-colors duration-500 z-10"></div>
          <img 
            src={image} 
            alt={imageAlt} 
            className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700" 
            loading="lazy"
          />
       </div>
    </div>
  </motion.div>
);

const About = () => {
  return (
    <>
      <Helmet>
        <title>Our Story & Mission | AGIR USA</title>
        <meta name="description" content="Discover the 25-year journey of AGIR USA, from a small feeding program to a holistic mission serving thousands in Guinea-Bissau." />
      </Helmet>

      {/* Hero Section */}
      <div className="relative py-32 md:py-48 flex items-center justify-center overflow-hidden min-h-[60vh]">
        <div className="absolute inset-0">
          <motion.img 
            initial={{ scale: 1.1 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1.5 }}
            src="https://media-manager-c.questera.ai/greta-media/b23737eec191c3f5b765303f85ce3a4932597ae054cc2b3dadfd019ed91c748ca1e6808982e16c775001fce05a7fb0b4/images/aW1hZ2UvanBlZw==/e06079f29fbfc9b22098a2faa4561dde.jpeg" 
            alt="Children sharing a meal" 
            className="w-full h-full object-cover" 
          />
          <div className="absolute inset-0 bg-primary-dark/80 mix-blend-multiply"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-primary-dark to-transparent"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10 text-center text-white">
          <motion.div 
            initial={{ opacity: 0, y: 30 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block py-1 px-4 border border-white/30 rounded-full text-sm font-bold tracking-widest uppercase mb-6 bg-white/10 backdrop-blur-md">
              Since 1998
            </span>
            <h1 className="font-heading text-5xl md:text-7xl font-bold mb-8 leading-tight">
              Faith in <span className="text-secondary italic">Action</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto leading-relaxed font-light drop-shadow-md">
              We don't just provide aid; we build families, nurture faith, and empower a generation to rise above poverty.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Mission & Vision - Split Layout */}
      <Section className="overflow-hidden">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-12"
          >
            <div className="relative pl-8 border-l-4 border-secondary">
              <div className="absolute -left-[22px] top-0 p-2 bg-white rounded-full border border-gray-100 shadow-sm">
                <SafeIcon icon={FaIcons.FaRocket} className="text-primary text-xl" />
              </div>
              <h2 className="font-heading text-3xl font-bold text-primary-dark mb-4">Our Mission</h2>
              <p className="text-gray-600 text-lg leading-relaxed">
                To glorify God by meeting the physical, educational, and spiritual needs of vulnerable children in Guinea-Bissau, equipping them with the tools to build a sustainable future.
              </p>
            </div>
            
            <div className="relative pl-8 border-l-4 border-accent">
              <div className="absolute -left-[22px] top-0 p-2 bg-white rounded-full border border-gray-100 shadow-sm">
                <SafeIcon icon={FaIcons.FaEye} className="text-accent text-xl" />
              </div>
              <h2 className="font-heading text-3xl font-bold text-primary-dark mb-4">Our Vision</h2>
              <p className="text-gray-600 text-lg leading-relaxed">
                A Guinea-Bissau where every child is fed, educated, and grounded in the love of Christ, becoming a leader who transforms their community.
              </p>
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="grid grid-cols-2 gap-6">
              <img src="https://media-manager-c.questera.ai/greta-media/b23737eec191c3f5b765303f85ce3a4932597ae054cc2b3dadfd019ed91c748ca1e6808982e16c775001fce05a7fb0b4/images/aW1hZ2UvanBlZw==/1485015ec55730083f0618a640d8fc98.jpeg" alt="Student working" className="rounded-2xl shadow-2xl mt-12 w-full object-cover aspect-[3/4]" />
              <img src="https://media-manager-c.questera.ai/greta-media/b23737eec191c3f5b765303f85ce3a4932597ae054cc2b3dadfd019ed91c748ca1e6808982e16c775001fce05a7fb0b4/images/aW1hZ2UvanBlZw==/dc7eb88b5901d7044887350acce3d65c.jpeg" alt="Child eating" className="rounded-2xl shadow-2xl w-full object-cover aspect-[3/4]" />
            </div>
            {/* Decorative element */}
            <div className="absolute -z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[80%] bg-surface-dark rounded-[3rem] -rotate-3"></div>
          </motion.div>
        </div>
      </Section>

      {/* History / Timeline */}
      <Section bg="surface" className="relative">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-primary-dark mb-4">Our Journey of Hope</h2>
            <p className="text-gray-600 text-lg">From humble beginnings to nationwide impact.</p>
          </div>
          
          <div className="relative">
            {/* Center Line */}
            <div className="absolute left-4 md:left-1/2 transform md:-translate-x-1/2 h-full w-0.5 bg-gray-200"></div>
            
            <TimelineItem 
              index={0}
              year="1998" 
              title="A Seed is Planted" 
              desc="AGIR USA is founded in response to the devastating civil war in Guinea-Bissau. We began by providing emergency food relief to displaced families." 
              align="left" 
              image="https://media-manager-c.questera.ai/greta-media/b23737eec191c3f5b765303f85ce3a4932597ae054cc2b3dadfd019ed91c748ca1e6808982e16c775001fce05a7fb0b4/images/aW1hZ2UvanBlZw==/e85f8df4ca1088998577b5cca07c0363.jpeg"
              imageAlt="Smiling child representing the beginning of AGIR USA’s mission in 1998"
            />
            <TimelineItem 
              index={1}
              year="2005" 
              title="Education Focus" 
              desc="Realizing that food alone wasn't enough, we opened our first supported school center, Escola Cristã Barca de Noé, offering free education." 
              align="right" 
              image="https://media-manager-c.questera.ai/greta-media/b23737eec191c3f5b765303f85ce3a4932597ae054cc2b3dadfd019ed91c748ca1e6808982e16c775001fce05a7fb0b4/images/aW1hZ2UvanBlZw==/7ab728fa003eb0944fc5dd69f70d57d4.jpeg"
              imageAlt="Students raising hands in a classroom, eager to learn"
            />
            <TimelineItem 
              index={2}
              year="2015" 
              title="Expanding Reach" 
              desc="Our feeding program scaled to serve over 500 children daily, partnering with local farmers to ensure sustainable food sources." 
              align="left" 
              image="https://media-manager-c.questera.ai/greta-media/b23737eec191c3f5b765303f85ce3a4932597ae054cc2b3dadfd019ed91c748ca1e6808982e16c775001fce05a7fb0b4/images/aW1hZ2UvanBlZw==/5cb8114a405a2170c4ce4c44746d842d.jpeg"
              imageAlt="Community members sharing a meal together in celebration"
            />
            <TimelineItem 
              index={3}
              year="2024" 
              title="Looking Forward" 
              desc="Today, we support over 1,000 children daily. We are launching new vocational training programs to bridge the gap between school and employment." 
              align="right" 
              image="https://media-manager-c.questera.ai/greta-media/b23737eec191c3f5b765303f85ce3a4932597ae054cc2b3dadfd019ed91c748ca1e6808982e16c775001fce05a7fb0b4/images/aW1hZ2UvanBlZw==/e635d115a90b5a90393c5c4e599b9e06.jpeg"
              imageAlt="Graduates smiling proudly, representing the future of Guinea-Bissau"
            />
          </div>
        </div>
      </Section>

      {/* Core Values */}
      <Section>
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="font-heading text-4xl font-bold text-primary-dark mb-4">What Drives Us</h2>
          <p className="text-gray-600 text-lg">Our values are the compass that guides every decision we make.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {[
            { icon: FaIcons.FaHandHoldingHeart, title: "Compassion", desc: "We don't just see statistics; we see children made in God's image. We serve with empathy, dignity, and unconditional love." },
            { icon: FaIcons.FaBalanceScale, title: "Integrity", desc: "We are accountable stewards. We believe in radical transparency with our donors and honest relationships with our beneficiaries." },
            { icon: FaIcons.FaSeedling, title: "Sustainability", desc: "We avoid dependency. Our programs are designed to empower communities to eventually stand on their own." }
          ].map((val, idx) => (
            <motion.div 
              key={idx} 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              whileHover={{ y: -10 }}
              className="bg-white p-10 rounded-2xl shadow-card border border-gray-100 text-center group"
            >
              <div className="w-20 h-20 mx-auto bg-surface rounded-full flex items-center justify-center text-secondary text-4xl mb-6 group-hover:bg-secondary group-hover:text-white transition-all duration-300">
                <SafeIcon icon={val.icon} />
              </div>
              <h3 className="font-heading text-2xl font-bold text-primary-dark mb-4">{val.title}</h3>
              <p className="text-gray-600 leading-relaxed">{val.desc}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Quote / Testimony */}
      <Section bg="primary" className="text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')]"></div>
        <div className="max-w-4xl mx-auto relative z-10">
          <SafeIcon icon={FaIcons.FaQuoteLeft} className="text-secondary/60 text-6xl mx-auto mb-8" />
          <motion.p 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className="text-2xl md:text-4xl font-heading font-bold text-white mb-10 leading-relaxed"
          >
            "I used to worry about where my next meal would come from. Now, I worry about my math test. AGIR USA didn't just give me food; they gave me a childhood."
          </motion.p>
          <div className="flex flex-col items-center gap-2">
            <div className="w-16 h-16 rounded-full bg-gray-300 overflow-hidden border-2 border-secondary mb-2">
              <img src="https://media-manager-c.questera.ai/greta-media/b23737eec191c3f5b765303f85ce3a4932597ae054cc2b3dadfd019ed91c748ca1e6808982e16c775001fce05a7fb0b4/images/aW1hZ2UvanBlZw==/e635d115a90b5a90393c5c4e599b9e06.jpeg" alt="Manuel" className="w-full h-full object-cover" />
            </div>
            <div className="text-center">
              <div className="font-bold text-secondary text-xl">Manuel</div>
              <div className="text-white/80 text-sm tracking-wide uppercase">Former Student, Now University Graduate</div>
            </div>
          </div>
        </div>
      </Section>
    </>
  );
};

export default About;